import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { StorageFunctions } from '../../../general/storage';
import { ConfiguracaoItensNfeForm, ConfiguracaoItensNfeViewModel } from './model-configuracao-itens-nfe/ConfiguracaoItensNfeViewModel';
import { UnidadesEmpresaViewModel } from 'models/unidades-empresa/UnidadesEmpresaViewModel';
import { CodigosBeneficiosFiscaisService } from '../../../services/codigos-beneficios-fiscais.service';
import { firstValueFrom } from 'rxjs';
import { TipoSituacaoTributariaCOFINS, TipoSituacaoTributariaIPI, TipoSituacaoTributariaPIS } from '@models';
import { TipoNfe } from '../../../model/TipoNfe';
import { CClassTribViewModel, CstIBSCBSViewModel, IBSCBSService } from '../../../services/ibscbs.service';
import { UnidadesEmpresaParametrosService } from '../../../services/unidades-empresa-parametros.service';
import { EmissaoNfeService } from '../../../fiscal/nfe/emissao-nfe/emissao-nfe.service';
import { markFormGroupTouched } from '@utils';

@Component({
  selector: 'app-configuracao-itens-nfe',
  templateUrl: './configuracao-itens-nfe.component.html',
  styleUrls: ['./configuracao-itens-nfe.component.scss']
})
export class ConfiguracaoItensNfeComponent implements OnInit {
  private storageKey = 'ConfiguracaoItensNfeComponent';

  @Output() opened = new EventEmitter();
  @Output() closed = new EventEmitter();
  @Output() applied = new EventEmitter<ConfiguracaoItensNfeViewModel>();

  @Input() open = false;
  @Input() tipoNfeID: number = null;
  @Input() cst: string = null;
  @Input() cBenefDefault: () => string = null;
  @Input() cstDefault: () => string = null;
  @Input() unidadeEmpresa: UnidadesEmpresaViewModel = null;

  clienteSimples: boolean = false;
  cstsListCRTNormal = [
    { id: '0', nome: '00 - Tributada Integralmente' },
    { id: '10', nome: '10 - Tributada e com Cobrança do ICMS por Substituição Tributária' },
    { id: '20', nome: '20 - Com redução da Base de Cálculo' },
    { id: '40', nome: '40 - Isenta' },
    { id: '41', nome: '41 - Não Tributada' },
    { id: '51', nome: '51 - Diferimento' },
  ];

  cstsListCRTSimples = [
    { id: '101', nome: '101 - Tributada com Permissão de Crédito' },
    { id: '102', nome: '102 - Tributada sem Permissão de Crédito' },
    { id: '400', nome: '400 - Não tributada' },
    { id: '900', nome: '900 - Outros' },
  ];

  cstsIPI = new TipoSituacaoTributariaIPI().Listar();
  cstsPIS = new TipoSituacaoTributariaPIS().List();
  cstsCOFINS = new TipoSituacaoTributariaCOFINS().Listar();
  cstsIBSCBS: CstIBSCBSViewModel[] = [];
  listaCClassTribIBSCBS: CClassTribViewModel[] = [];
  cClassTribIBSCBS: CClassTribViewModel[] = [];
  parametrosNfe = null;

  loading = [];
  codigosCreditoPresumido = [];

  form = this.getFormDefault();

  constructor(
    private storage: StorageFunctions,
    private codigosBeneficiosFiscaisService: CodigosBeneficiosFiscaisService,
    private ibsCbsService: IBSCBSService,
    private unidadeEmpresaParametros: UnidadesEmpresaParametrosService,
    private emissaoNfeService: EmissaoNfeService,
  ) { }    

  get crtSimples() {
    return this.unidadeEmpresa && [1, 2].includes(this.unidadeEmpresa.crt);
  }

  get crtNormal() {
    return this.unidadeEmpresa && [3, 4].includes(this.unidadeEmpresa.crt);
  }

  ngOnInit(): void {
    this.carregaCodigoCreditoPresumido();
    this.setCstDefault();
    this.carregarDadosIBSCBS();
    this.unidadeEmpresaParametros.getParametrosNfe().subscribe(res => {
      this.parametrosNfe = res;
    });

    this.form.get('aplicarCst').valueChanges.subscribe((b: boolean) => {
      if (b) {
        this.form.get('cst').enable();
        this.setCstDefault();
      } else {
        this.form.get('cst').disable();
        this.form.get('cst').setValue(null);
        this.setDataToStorage();
      }
    });

    this.form.get('aplicarCCredPresumido').valueChanges.subscribe((b: boolean) => {
      if (b) {
        this.form.get('cCredPresumido').enable();
      } else {
        this.form.get('cCredPresumido').disable();
        this.form.get('cCredPresumido').setValue(null);
        this.setDataToStorage();
      }
    });

    this.form.get('aplicarIPI').valueChanges.subscribe((b: boolean) => {
      if (b) {
        this.form.get('cstIPI').enable();
        this.form.get('aliquotaIPI').enable();
      } else {
        this.form.get('cstIPI').disable();
        this.form.get('cstIPI').setValue(null);
        this.form.get('aliquotaIPI').disable();
        this.form.get('aliquotaIPI').setValue(null);
        this.setDataToStorage();
      }
    });

    this.form.get('aplicarPIS').valueChanges.subscribe((b: boolean) => {
      if (b) {
        this.form.get('cstPIS').enable();
        this.form.get('aliquotaPIS').enable();
      } else {
        this.form.get('cstPIS').disable();
        this.form.get('cstPIS').setValue(null);
        this.form.get('aliquotaPIS').disable();
        this.form.get('aliquotaPIS').setValue(null);
        this.setDataToStorage();
      }
    });

    this.form.get('aplicarCOFINS').valueChanges.subscribe((b: boolean) => {
      if (b) {
        this.form.get('cstCOFINS').enable();
        this.form.get('aliquotaCOFINS').enable();
      } else {
        this.form.get('cstCOFINS').disable();
        this.form.get('cstCOFINS').setValue(null);
        this.form.get('aliquotaCOFINS').disable();
        this.form.get('aliquotaCOFINS').setValue(null);
        this.setDataToStorage();
      }
    });

    this.form.get('aplicarIBSCBS').valueChanges.subscribe((b: boolean) => {
      if (b) {
        this.form.get('cstIBSCBS').enable();
        this.form.get('cstIBSCBS').setValidators([Validators.required]);
        this.form.get('cClassTribIBSCBS').enable();
        this.form.get('cClassTribIBSCBS').setValidators([Validators.required]);
      } else {
        this.form.get('cstIBSCBS').disable();
        this.form.get('cstIBSCBS').setValue(null);
        this.form.get('cstIBSCBS').clearValidators();
        this.form.get('cClassTribIBSCBS').disable();
        this.form.get('cClassTribIBSCBS').setValue(null);
        this.form.get('cClassTribIBSCBS').clearValidators();
        this.setDataToStorage();
      }

      this.form.get('cstIBSCBS').updateValueAndValidity();
      this.form.get('cClassTribIBSCBS').updateValueAndValidity();
    });

    this.form.get('cstIBSCBS').valueChanges.subscribe(res => {
      this.cClassTribIBSCBS = this.listaCClassTribIBSCBS.filter(x => x.cst === res);

      const cClassTrib = this.form.get('cClassTribIBSCBS').value;
      if (cClassTrib && !this.cClassTribIBSCBS.some(x => x.cClassTrib === cClassTrib)) {
        this.form.get('cClassTribIBSCBS').setValue(null);
      }
    });

    this.form.get('cClassTribIBSCBS').valueChanges.subscribe(res => {
      const cClassTrib = this.listaCClassTribIBSCBS.find(x => x.cClassTrib === res);
      this.form.get('pRedIBS').setValue(cClassTrib?.pRedIBS);
      this.form.get('pRedCBS').setValue(cClassTrib?.pRedCBS);
    });
  }

  getFormDefault(): FormGroup<ConfiguracaoItensNfeForm> {
    const f = new FormGroup({
      replicarParaNovosItens: new FormControl(false),
      aplicarCbenef: new FormControl(false),
      cBenef: new FormControl(null),
      aplicarCst: new FormControl(false),
      cst: new FormControl({ value: null, disabled: true }),
      aplicarCCredPresumido: new FormControl(false),
      cCredPresumido: new FormControl({ value: null, disabled: true }),
      aplicarIPI: new FormControl(false),
      cstIPI: new FormControl(null),
      aliquotaIPI: new FormControl(null),
      aplicarPIS: new FormControl(false),
      cstPIS: new FormControl(null),
      aliquotaPIS: new FormControl(null),
      aplicarCOFINS: new FormControl(false),
      cstCOFINS: new FormControl(null),
      aliquotaCOFINS: new FormControl(null),
      aplicarIBSCBS: new FormControl(null),
      cstIBSCBS: new FormControl(null),
      cClassTribIBSCBS: new FormControl(null),
      pRedIBS: new FormControl(null),
      pRedCBS: new FormControl(null),
      percDiferimentoIBSCBS: new FormControl(null),
    });

    return f;
  }

  onOpened() {
    this.setDataFromStorage();
    this.opened.emit();
  }

  onClosed() {
    this.closed.emit();
  }

  onSubmit() {
    markFormGroupTouched(this.form);
    if (!this.form.valid) {
      return;
    }

    this.setDataToStorage();
    this.applied.emit(this.form.getRawValue());
  }

  onCancel() {
    this.closed.emit();
  }

  private setCstDefault() {
    if (!this.cstDefault || typeof this.cstDefault !== 'function') {
      return;
    }

    this.cst = this.cstDefault();
    if (this.cst) {
      this.form.get('cst').setValue(this.cst);
      this.setDataToStorage();
    }
  }

  private setCBenefDefault() {
    if (!this.cBenefDefault || typeof this.cBenefDefault !== 'function') {
      return;
    }

    const cBenef = this.cBenefDefault();
    if (cBenef) {
      this.form.get('cBenef').setValue(cBenef);
      this.setDataToStorage();
    }
  }

  private setDataFromStorage() {
    const storageMapData = this.storage.getDataFromLocalStorage(this.storageKey) || {};
    if (this.tipoNfeID === null || !(this.tipoNfeID in storageMapData)) {
      this.setCBenefDefault();
    } else {
      this.form.patchValue(storageMapData[this.tipoNfeID]);
      this.form.get('replicarParaNovosItens').setValue(false);
    }
  }

  private setDataToStorage() {
    const storageMapData = this.storage.getDataFromLocalStorage(this.storageKey) || {};
    storageMapData[this.tipoNfeID] = this.form.getRawValue();
    this.storage.saveDataToLocalStorage(this.storageKey, storageMapData);
  }

  async carregaCodigoCreditoPresumido() {
    try {
      this.loading['codigosCreditoPresumido'] = true;
      this.codigosCreditoPresumido = await firstValueFrom(this.codigosBeneficiosFiscaisService.getCreditoPresumido());
    } catch (error) {
      console.error(error);
    } finally {
      this.loading['codigosCreditoPresumido'] = false;
    }
  }

  get notaEntradaImportacao() {
    return this.tipoNfeID == TipoNfe.IMPORTACAO;
  }

  get permiteImpostosRtc() {
    return this.emissaoNfeService.permiteImpostosRtc(this.unidadeEmpresa?.crt);
  }

  carregarDadosIBSCBS() {
    this.ibsCbsService.buscarCst('nfe').subscribe(res => {
      this.cstsIBSCBS = res;
    });

    this.ibsCbsService.buscarCClassTrib().subscribe(res => {
      this.listaCClassTribIBSCBS = res.filter(x => x.indNFe);
    });
  }

  buscaCstIBSCBS(search, item) {
    return `${item.cst} - ${item.descricao}`.toLowerCase().includes(search.toLowerCase());
  }

  buscaCClassTribIBSCBS(search, item) {
    return `${item.cClassTrib} - ${item.nome}`.toLowerCase().includes(search.toLowerCase());
  }
}
